import * as XLSX from "xlsx";
import type { NetworkFlagCell, NetworkRiskFlagsData } from "@services/networkDashboard";
import { formatFacilityDisplayName } from "@utils/facilityUtils";

const FLAG_COLUMNS: { key: string; header: string }[] = [
  { key: "sff", header: "SFF" },
  { key: "abuse", header: "Abuse" },
  { key: "survey_age", header: "Survey" },
  { key: "ownership", header: "Ownership" },
  { key: "sprinkler", header: "Sprinkler" },
  { key: "family_council", header: "Council" },
  { key: "admin_churn", header: "Admin" },
];

const FLAG_FILTER_LABELS: Record<string, string> = {
  sff: "Special_Focus_Status",
  abuse: "Abuse",
  survey_age: "Survey_Over_2yr",
  ownership: "Ownership_Change",
  sprinkler: "Sprinkler",
  family_council: "No_Family_Council",
  admin_churn: "Admin_Turnover",
};

type FacilityLocation = {
  ccn?: string | null;
  provider_address?: string | null;
  address?: string | null;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
};

function pickNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function lookupCcnLocation(
  ccn: string,
  locationByCcn?: Record<string, FacilityLocation>,
): FacilityLocation | undefined {
  if (!locationByCcn || !ccn) return undefined;
  const trimmed = ccn.trim();
  if (locationByCcn[trimmed]) return locationByCcn[trimmed];
  if (/^\d+$/.test(trimmed)) {
    const padded = trimmed.padStart(6, "0");
    if (locationByCcn[padded]) return locationByCcn[padded];
    const unpadded = trimmed.replace(/^0+/, "") || "0";
    if (locationByCcn[unpadded]) return locationByCcn[unpadded];
  }
  for (const [key, value] of Object.entries(locationByCcn)) {
    if (key.replace(/^0+/, "") === trimmed.replace(/^0+/, "")) {
      return value;
    }
  }
  return undefined;
}

function formatExportLocation(parts: string[]): string {
  return parts.filter(Boolean).join(", ");
}

export function formatNetworkRiskFlagCell(cell?: NetworkFlagCell): string {
  if (!cell || cell.status === "neutral") return "—";
  const statusLabel =
    cell.status === "alert" ? "Alert" : cell.status === "warn" ? "Warning" : "OK";
  if (cell.value != null && cell.value !== "") {
    return `${statusLabel} (${cell.value})`;
  }
  return statusLabel;
}

export function buildNetworkRiskFlagsExportRows(
  data: NetworkRiskFlagsData,
  facilityLocations?: Record<string, FacilityLocation>,
  facilityNameToCcn?: Record<string, string>,
): Record<string, string | number>[] {
  return data.facilities.map((facility) => {
    let ccn = pickNonEmptyString(facility.ccn);
    if (!ccn && facilityNameToCcn) {
      const nameKey = pickNonEmptyString(facility.provider_name).toLowerCase();
      if (nameKey) ccn = pickNonEmptyString(facilityNameToCcn[nameKey]);
    }
    const loc = lookupCcnLocation(ccn, facilityLocations);
    const address = pickNonEmptyString(
      facility.provider_address,
      loc?.provider_address,
      loc?.address,
      loc?.address_1,
    );
    const city = pickNonEmptyString(facility.city, loc?.city);
    const state = pickNonEmptyString(facility.state, loc?.state);

    const row: Record<string, string | number> = {
      CCN: ccn,
      "Facility Name": formatFacilityDisplayName(String(facility.provider_name ?? "")),
      Location: formatExportLocation([address, city, state]),
      City: city,
      State: state,
      Beds: facility.beds ?? "",
      "Risk Score": facility.risk_score,
    };
    for (const { key, header } of FLAG_COLUMNS) {
      row[header] = formatNetworkRiskFlagCell(facility.flags[key]);
    }
    return row;
  });
}

function sanitizeFileToken(value: string): string {
  return value.replace(/[^\w.-]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
}

export function networkRiskFlagsExportFileName(options?: {
  networkName?: string;
  flagFilter?: string | null;
  reportingPeriodLabel?: string;
}): string {
  const parts = ["Network_Risk_Flags"];
  if (options?.networkName) {
    parts.push(sanitizeFileToken(options.networkName));
  }
  if (options?.flagFilter) {
    parts.push(FLAG_FILTER_LABELS[options.flagFilter] ?? sanitizeFileToken(options.flagFilter));
  }
  if (options?.reportingPeriodLabel) {
    parts.push(sanitizeFileToken(options.reportingPeriodLabel));
  }
  const date = new Date().toISOString().slice(0, 10);
  return `${parts.join("_")}_${date}.xlsx`;
}

export function exportNetworkRiskFlagsToExcel(
  data: NetworkRiskFlagsData,
  options?: {
    facilityLocations?: Record<string, FacilityLocation>;
    facilityNameToCcn?: Record<string, string>;
    networkName?: string;
    flagFilter?: string | null;
    reportingPeriodLabel?: string;
  },
): void {
  const rows = buildNetworkRiskFlagsExportRows(
    data,
    options?.facilityLocations,
    options?.facilityNameToCcn,
  );
  if (!rows.length) return;

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Risk Flags");
  XLSX.writeFile(
    workbook,
    networkRiskFlagsExportFileName({
      networkName: options?.networkName,
      flagFilter: options?.flagFilter,
      reportingPeriodLabel: options?.reportingPeriodLabel,
    }),
  );
}
